import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Container, Row, Col, CardHeader, Card, CardBody, FormSelect, FormInput, Button, ButtonGroup, Modal, ModalHeader, ModalBody, ModalFooter } from "shards-react";
import 'chart.js/auto';
import { Chart } from 'react-chartjs-2';
import { accessCheck } from "../../utils/tools";
import Select from "react-select";
import DatePicker from "react-datepicker";

import PageTitle from "../../components/common/PageTitle";
import SmallStats from "../../components/common/SmallStats";
import ActivityIndicator from "../../components/common/ActivityIndicator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faEllipsisH, faCalendar, faCheckCircle, faChartBar, faFileInvoiceDollar, faCarAlt, faExternalLinkAlt, faEye, faRocket, faLightbulb, faQuestionCircle, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import EnhancedDateRangePicker from "../../components/common/EnhancedDateRangePicker";
import { startOfMonth, endOfMonth, startOfDay, endOfDay, sub, startOfWeek, endOfWeek, formatISO } from "date-fns";
import DisputeQuickView from "../Citations/DisputeQuickView";

import "react-datepicker/dist/react-datepicker.css";

const moment = require('moment');

const Dashboard = (props) => {

  const [startDate, setStartDate] = useState(() => {
    // Check if there's a saved date range in localStorage
    const savedStartDate = localStorage.getItem('enforcePlus_startDate');
    return savedStartDate ? new Date(savedStartDate) : startOfMonth(new Date());
  });
  const [endDate, setEndDate] = useState(() => {
    // Check if there's a saved date range in localStorage
    const savedEndDate = localStorage.getItem('enforcePlus_endDate');
    return savedEndDate ? new Date(savedEndDate) : endOfMonth(new Date());
  });
  const [location, setLocation] = useState('all')
  const [locations, setLocations] = useState(props.locations)
  const [client, setClient] = useState(props.user.type === 'super' ? 'all' : props.clients && props.clients.length > 0 ? props.clients[0].id : null)
  const [dailyScans, setDailyScans] = useState([])
  const [citations, setCitations] = useState([])
  const [warnings, setWarnings] = useState([])
  const [scans, setScans] = useState([])
  const [disputes, setDisputes] = useState([])
  const [users, setUsers] = useState(props.users)
  const [user, setUser] = useState('all')
  const [showAppBanner, setShowAppBanner] = useState(() => {
    // Check if on mobile device - hide banner on mobile
    const isMobile = window.innerWidth < 768;
    return !isMobile;
  });
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(() => {
    // Check if on first visit
    const firstVisit = localStorage.getItem('enforcePlus_firstVisit') === null;
    return firstVisit;
  });
  const [showDatePickerModal, setShowDatePickerModal] = useState(false);
  const [activeCalendar, setActiveCalendar] = useState(null);

  const chartOptions = {
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#495057',
        bodyColor: '#495057',
        borderColor: '#e9ecef',
        borderWidth: 1,
        padding: 10,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        cornerRadius: 4,
        displayColors: true
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        }
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          precision: 0
        }
      },
      myScale: {
        position: 'right'
      }
    },
    
  }

  useEffect(() => {
    document.title = 'Dashboard: EnforcePlus';
    if (props.user?.type === 'tenant') {
      window.location.href = '/tenant-vehicles'
    } else {
      !accessCheck(props.user, 'dashboard') && (window.location.href = '/')
    }
    // props.runCustomQuery()
    props.getClients()
    props.getUsers()
    props.getLocations()
    props.clearCitation()
    
    // Add window resize listener to hide app banner on mobile
    const handleResize = () => {
      setShowAppBanner(window.innerWidth >= 768);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Clean up event listener
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [])

  useEffect(() => {
    if (location && client && startDate && endDate && user) {
      props.getCitations(location !== 'all' ? location : null, client !== 'all' ? client : null, formatISO(startDate), formatISO(endDate), user !== 'all' ? user : null)
      props.getDisputes(location !== 'all' ? location : null, client !== 'all' ? client : null, formatISO(startDate), formatISO(endDate), user !== 'all' ? user : null)
      props.getDailyScanTypes(location !== 'all' ? location : null, client !== 'all' ? client : null, formatISO(startDate), formatISO(endDate), user !== 'all' ? user : null)
      props.getDailyScanCounts(location !== 'all' ? location : null, client !== 'all' ? client : null, formatISO(startDate), formatISO(endDate), user !== 'all' ? user : null)
      if (client !== 'all' && props.locations && props.locations > 0) {
        setLocations(props.locations.filter(location => location.client === client))
      } else {
        setLocations(props.locations)
      }
    }
    
  }, [location, client, startDate, endDate, user])

  useEffect(() => {
    setDailyScans(props.dailyScans)
  }, [props.dailyScans])

  useEffect(() => {
    setLocations(props.locations)
  }, [props.locations])

  useEffect(() => {
    setUsers(props.users)
  }, [props.users])

  useEffect(() => {
    setScans(props.scans)
  }, [props.scans])

  useEffect(() => {
    setCitations(props.citations)
    // More comprehensive debugging
    console.log("Citations data (length):", props.citations?.length);
    console.log("Citations data (sample):", props.citations?.[0]);
    
    // Show structure analysis if data exists
    if (props.citations && props.citations.length > 0) {
      const sample = props.citations[0];
      console.log("Citation structure:", {
        hasId: !!sample.id,
        hasViolationNumber: !!sample.violation_number,
        hasLicensePlate: !!sample.license_plate,
        hasVehicle: !!sample.vehicle,
        hasTotal: !!sample.total,
        hasCreatedAt: !!sample.created_at,
        hasDate: !!sample.date,
        hasStatus: !!sample.status,
        status: sample.status,
        isArray: Array.isArray(props.citations),
        typeofData: typeof props.citations
      });
    }
  }, [props.citations])

  useEffect(() => {
    let disputesTemp = props.disputes?.length > 0 ? props.disputes?.filter(citation => citation?.disputed_on && citation?.dispute?.status === 'open') : []
    disputesTemp = disputesTemp.sort((a, b) => moment.utc(a.disputed_on).isBefore(moment.utc(b.disputed_on)) ? 1 : -1)
    setDisputes(disputesTemp)
  }, [props.disputes])

  useEffect(() => {
    setWarnings(props.warnings)
  }, [props.warnings])

  // Handle welcome modal on first visit
  useEffect(() => {
    // Check if it's the first visit
    const firstVisit = localStorage.getItem('enforcePlus_firstVisit') === null;
    
    if (firstVisit) {
      setShowWelcomeModal(true);
    }
  }, [])

  const getDateLabels = () => {
    let datesArray = []
    for (let m = moment(startDate); m.diff(endDate, 'days') <= 0; m.add(1, 'days')) {
      if (!m.isAfter(endDate)) {
        datesArray.push(m.format("M/D"))
      }
    }
    return datesArray
  }

  const calculateCitationDoughnut = () => {
    let paid = citations.filter(cit => cit.status === 'paid')?.map(cit => parseFloat((cit.paid_total))).reduce((total, item) => total + item, 0) ?? 0
    let unpaid = citations.filter(cit => cit.status === 'unpaid' && cit.ticket_type !== 'warning')?.map(cit => parseFloat((cit.total))).reduce((total, item) => total + item, 0) ?? 0
    let voidAmt = citations.filter(cit => cit.status === 'void')?.map(cit => parseFloat((cit.violations_total))).reduce((total, item) => total + item, 0) ?? 0
    let closed = citations.filter(cit => cit.status === 'closed')?.map(cit => parseFloat((cit.violations_total))).reduce((total, item) => total + item, 0) ?? 0
    // citations.map(citation => {
    //   if (citation.status !== 'closed') {
    //     if (citation?.payment?.amount && citation.status !== 'void') {
    //       paid = paid + parseFloat(parseFloat(citation.payment.amount) - parseFloat(citation.service_fee))
    //     } else if (!citation?.payment?.amount && citation.status !== 'void') {
    //       unpaid = unpaid + (citation.total > 0 ? parseFloat(citation.total) : 0)
    //     } else {
    //       voidAmt = voidAmt + parseFloat(citation.violations_total)
    //     }
    //   } else {
    //     closed = closed + parseFloat(citation.violations_total)
    //   }
    // })
    return {paid, unpaid, voidAmt, closed}
  }

  const calculateCitationStats = () => {
    let paid = citations.filter(cit => cit.status === 'paid')?.map(cit => (cit.count)).reduce((total, item) => total + item, 0) ?? 0
    let unpaid = citations.filter(cit => cit.status === 'unpaid' && cit.ticket_type !== 'warning')?.map(cit => (cit.count)).reduce((total, item) => total + item, 0) ?? 0
    let voidAmt = citations.filter(cit => cit.status === 'void')?.map(cit => (cit.count)).reduce((total, item) => total + item, 0) ?? 0
    let closed = citations.filter(cit => cit.status === 'closed')?.map(cit => (cit.count)).reduce((total, item) => total + item, 0) ?? 0
    let warningsCount = citations.filter(cit => cit.status === 'unpaid' && cit.ticket_type === 'warning')?.map(cit => (cit.count)).reduce((total, item) => total + item, 0) ?? 0
    // citations.map(citation => {
    //   if (citation.status !== 'closed') {
    //     if (citation?.payment?.amount && citation.status !== 'void') {
    //       paid = paid + 1
    //     } else if (citation?.payment?.amount && citation.status !== 'void') {
    //       unpaid = unpaid + 1
    //     } else {
    //       voidAmt = voidAmt + 1
    //     }
    //   } else {
    //     closed = closed + 1
    //   }
    // })
    return {paid, unpaid, voidAmt, closed, warningsCount}
  }

  const calculateScanStats = () => {
    let scan = scans.find(item => item.name === 'scan')?.count || 0
    let violation = scans.find(item => item.name === 'violation')?.count || 0
    let citation = scans.find(item => item.name === 'citation')?.count || 0
    violation = violation + citation
    return {scan, violation}
  }

  const renderDisputes = () => {
    if (props.gettingCitations) {
      return (
        <Row>
          <Col className={'text-center'}>
            <ActivityIndicator />
          </Col>
        </Row>
      )
    } 
    return disputes.map(citation => {
      return (
        <Row style={{fontSize: 11, marginBottom: 8}}>
          <Col xs={6} sm={4}>
            {citation.dispute.name}<br />
            <span style={{fontSize: 9, color: '#ADAEB2'}}>{citation.violation_number}</span>
          </Col>
          <Col xs={6} sm={4}>
            {moment(citation.disputed_on).format('MM/DD/Y')}<br />
            <span style={{fontSize: 9, color: '#E6492E'}}>Received {moment(citation.disputed_on).fromNow()}</span>
          </Col>
          <Col xs={4} sm={1}></Col>
          <Col xs={4} sm={2}>${parseFloat(citation.total).toFixed(0)}</Col>
          <Col xs={4} sm={1}>
            <span className="ellipsis-menu" onClick={() => window.location.href = '/citation/' + citation.violation_number}>
              <FontAwesomeIcon icon={faEllipsisH} size={10} />
            </span>
          </Col>
        </Row>
      )
    })
  }

  let {paid: donutPaid, unpaid: donutUnpaid, voidAmt: donutVoid, closed: donutClosed} = calculateCitationDoughnut()
  let {paid: statsPaid, unpaid: statsUnpaid, voidAmt: statsVoid, closed: statsClosed, warningsCount: statsWarnings} = calculateCitationStats()

  // Add these handlers for dispute actions
  const handleAcceptDispute = (citation, note) => {
    props.acceptDispute(citation, note);
  };

  const handleRejectDispute = (citation, note) => {
    props.rejectDispute(citation, note);
  };

  const handleAdjustDispute = (citation, amount, note) => {
    props.adjustDispute(citation, amount, note);
  };

  const handleViewFullCitation = (citation) => {
    window.location.href = '/citation/' + citation.violation_number;
  };

  const viewDisputeDetails = (citation) => {
    setSelectedDispute(citation);
    setShowDisputeModal(true);
  };
  
  const closeWelcomeModal = (permanent = false) => {
    // Mark first visit as complete only if permanent dismissal
    if (permanent) {
      localStorage.setItem('enforcePlus_firstVisit', 'complete');
    }
    setShowWelcomeModal(false);
  };

  const fixTenants = () => {
    if (window.confirm('This will fix existing tenants that don\'t have the active field set. Continue?')) {
      props.fixExistingTenants();
    }
  };

  const checkTenantAuth = () => {
    const email = prompt('Enter tenant email to check:');
    if (email) {
      props.checkUserInFirebaseAuth(email);
    }
  };

  const createTenantAuth = () => {
    const email = prompt('Enter tenant email:');
    const password = prompt('Enter password for tenant:');
    if (email && password) {
      props.createFirebaseAuthUser(email, password);
    }
  };

  const handleDateChange = (type, date) => {
    if (type === 'start') {
      setStartDate(date);
    } else if (type === 'end') {
      setEndDate(date);
    }
    // Save to localStorage
    localStorage.setItem('enforcePlus_startDate', date.toISOString());
    localStorage.setItem('enforcePlus_endDate', date.toISOString());
    setShowDatePickerModal(false);
  };

  return (
    <Container fluid className="main-content-container px-4">
      <style>
        {`
          .filter-active .select__control {
            border-color: #21AA73 !important;
            border-width: 2px !important;
            box-shadow: 0 0 5px rgba(33, 170, 115, 0.2) !important;
            background-color: rgba(33, 170, 115, 0.05) !important;
          }
        `}
      </style>
      {/* Page Header */}
      <Row noGutters className="page-header py-4">
        <Col xs="12" sm="4" className="d-flex align-items-center mb-4 mb-sm-0">
          <PageTitle title="Dashboard" className="text-sm-left mb-0" />
        </Col>
        <Col xs="12" sm="8" className="mt-4 mt-sm-0">
          <Row className="align-items-center">
            <Col md="4" className="mb-3 mb-md-0">
              <Button
                onClick={() => setShowDatePickerModal(true)}
                theme="white"
                style={{
                  height: '40px',
                  borderRadius: '8px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  color: '#495057',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 15px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#F8FAFC',
                    borderColor: '#E2E8F0'
                  }
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#495057' }}>
                  <FontAwesomeIcon icon={faCalendar} className="mr-2" style={{ color: '#21AA73' }} />
                  {moment(startDate).format('MMM D')} - {moment(endDate).format('MMM D')}
                </span>
                <FontAwesomeIcon icon={faCaretDown} style={{ color: '#64748B', fontSize: '12px' }} />
              </Button>
            </Col>
            {props.user.type === 'super' && (
              <Col md="3" className="mb-3 mb-md-0">
                <Select
                  placeholder="Filter by client"
                  value={{ value: client, label: client === 'all' ? 'All Clients' : props.clients.find(c => c.id === client)?.name }}
                  onChange={(selected) => setClient(selected.value)}
                  options={[
                    { value: 'all', label: 'All Clients' },
                    ...props.clients.map(client => ({
                      value: client.id,
                      label: client.name
                    }))
                  ]}
                  styles={{
                    control: (base) => ({
                      ...base,
                      height: '40px',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      backgroundColor: '#FFFFFF',
                      '&:hover': {
                        border: '1px solid #E2E8F0'
                      }
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#21AA73' : state.isFocused ? '#F0FDF4' : 'white',
                      '&:active': {
                        backgroundColor: '#21AA73'
                      }
                    })
                  }}
                />
              </Col>
            )}
            <Col md={props.user.type === 'super' ? "3" : "4"} className="mb-3 mb-md-0">
              <Select
                placeholder="Filter by location"
                value={{ value: location, label: location === 'all' ? 'All Locations' : locations.find(l => l.id === location)?.name }}
                onChange={(selected) => setLocation(selected.value)}
                options={[
                  { value: 'all', label: 'All Locations' },
                  ...locations.map(location => ({
                    value: location.id,
                    label: location.name
                  }))
                ]}
                styles={{
                  control: (base) => ({
                    ...base,
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    backgroundColor: '#FFFFFF',
                    '&:hover': {
                      border: '1px solid #E2E8F0'
                    }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? '#21AA73' : state.isFocused ? '#F0FDF4' : 'white',
                    '&:active': {
                      backgroundColor: '#21AA73'
                    }
                  })
                }}
              />
            </Col>
            <Col md={props.user.type === 'super' ? "2" : "4"}>
              <Select
                placeholder="Filter by user"
                value={{ value: user, label: user === 'all' ? 'All Users' : users.find(u => u.id === user)?.name }}
                onChange={(selected) => setUser(selected.value)}
                options={[
                  { value: 'all', label: 'All Users' },
                  ...users.map(user => ({
                    value: user.id,
                    label: user.name
                  }))
                ]}
                styles={{
                  control: (base) => ({
                    ...base,
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    backgroundColor: '#FFFFFF',
                    '&:hover': {
                      border: '1px solid #E2E8F0'
                    }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? '#21AA73' : state.isFocused ? '#F0FDF4' : 'white',
                    '&:active': {
                      backgroundColor: '#21AA73'
                    }
                  })
                }}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      
      {/* App Download Banner */}
      {showAppBanner && (
        <div className="app-download-banner">
          <div className="banner-text">
            <h5>EnforcePlus: Mobile ALPR & LPR</h5>
            <p>You can find the "EnforcePlus: Mobile ALPR & LPR" app on the App Stores. Enforce Plus is everything you need to elevate your parking enforcement in one powerful software suite and accurate LPR technology.</p>
          </div>
          <div className="store-buttons">
            <a href="https://apps.apple.com/us/app/enforceplus" target="_blank" rel="noopener noreferrer" className="store-button">
              <i className="fab fa-apple"></i>
              <span>App Store</span>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.enforceplus" target="_blank" rel="noopener noreferrer" className="store-button">
              <i className="fab fa-google-play"></i>
              <span>Google Play</span>
            </a>
          </div>
          <button className="dismiss-button" onClick={() => setShowAppBanner(false)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      

     {(!props.clients || props.clients.length === 0 || !props.locations || !props.users) ? (
       <div className="loading-container text-center py-5">
         <ActivityIndicator size="large" />
         <p className="mt-3 text-muted">Loading dashboard data...</p>
       </div>  
     ) : (
       <>
        <Row className="mb-4">
          <Col md="6" className="col-12 mb-4">
            <Card className={'h-100'} style={{borderRadius: '6px', boxShadow: '0 1px 10px rgba(0,0,0,0.05)', border: '1px solid #eaedf3'}}>
              <CardHeader className="border-bottom">
                <Row>
                  <Col xs="6">
                    <h6 className="m-0 font-weight-bold d-flex align-items-center">
                      <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" style={{color: '#E6492E', opacity: 0.8}} />
                      Disputes
                    </h6>
                    <small className="text-muted">Open disputes requiring attention</small>
                  </Col>
                  <Col xs="6" className={'text-right'}>
                    <a href='/citations?view=disputed' className="see-more-link">
                      See More&nbsp;&nbsp;&nbsp;
                      <FontAwesomeIcon icon={faArrowRight} size={10} className="arrow-icon" style={{transition: 'transform 0.2s ease'}} />
                    </a>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody style={{paddingTop: 12, height: 235, maxHeight: 235, overflowY: 'auto'}}>
                {props.gettingDisputes ? (
                  <Row>
                    <Col className={'text-center'}>
                      <ActivityIndicator />
                    </Col>
                  </Row>
                ) : disputes?.length > 0 ? (
                  disputes.map(citation => (
                    <div 
                      key={citation.id}
                      className="dispute-item p-2 mb-2" 
                      style={{
                        borderRadius: '6px',
                        border: '1px solid #f0f0f0',
                        background: '#ffffff',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                        cursor: 'pointer'
                      }}
                    >
                      <Row style={{fontSize: 12}}>
                        <Col xs={7} onClick={() => window.location.href = '/citation/' + citation.violation_number}>
                          <div className="font-weight-bold">{citation.dispute.name}</div>
                          <div className="text-muted" style={{fontSize: 10}}>{citation.violation_number}</div>
                        </Col>
                        <Col xs={5} className="text-right">
                          <div className="font-weight-bold">${parseFloat(citation.total).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}</div>
                          <div>
                            <span className="badge" style={{
                              backgroundColor: moment(citation.disputed_on).isAfter(moment().subtract(2, 'days')) ? '#FADBD8' : '#F5B7B1',
                              color: '#E6492E',
                              fontSize: '9px',
                              padding: '3px 6px',
                              borderRadius: '12px'
                            }}>
                              {moment(citation.disputed_on).isAfter(moment().subtract(1, 'days')) ? 'NEW ' : ''}
                              {moment(citation.disputed_on).fromNow()}
                            </span>
                          </div>
                        </Col>
                      </Row>
                      <Row style={{fontSize: 11, marginTop: '4px'}}>
                        <Col xs={8} onClick={() => window.location.href = '/citation/' + citation.violation_number}>
                          <span className="text-muted">Received: {moment(citation.disputed_on).format('MM/DD/Y')}</span>
                        </Col>
                        <Col xs={4} className="text-right">
                          <div className="d-flex justify-content-end">
                            <Button
                              size="sm"
                              theme="light"
                              style={{
                                padding: '2px 8px',
                                fontSize: '10px',
                                marginRight: '8px',
                                borderRadius: '3px'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                viewDisputeDetails(citation);
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} className="mr-1" style={{fontSize: '9px'}} />
                              Quick
                            </Button>
                            <Button
                              size="sm"
                              theme="light"
                              style={{
                                padding: '2px 8px',
                                fontSize: '10px',
                                borderRadius: '3px'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.location.href = '/citation/' + citation.violation_number;
                              }}
                            >
                              <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" style={{fontSize: '9px'}} />
                              Full
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  ))
                ) : (
                  <div className="text-center p-4" style={{color: '#6c757d'}}>
                    <FontAwesomeIcon icon={faCheckCircle} size="2x" style={{color: '#21AA73', marginBottom: '10px', opacity: 0.8}} />
                    <h6>No open disputes</h6>
                    <p style={{fontSize: '12px'}}>All disputes have been resolved for the selected period.</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col md="6" className="col-12 mb-4">
            <Card className={'h-100'} style={{borderRadius: '6px', boxShadow: '0 1px 10px rgba(0,0,0,0.05)', border: '1px solid #eaedf3'}}>
              <CardHeader className="border-bottom">
                <Row>
                  <Col xs="6">
                    <h6 className="m-0 font-weight-bold d-flex align-items-center">
                      <FontAwesomeIcon icon={faChartBar} className="mr-2" style={{color: '#21AA73', opacity: 0.8}} />
                      Citations Issued
                    </h6>
                    <small className="text-muted">Financial breakdown by status</small>
                  </Col>
                  <Col xs="6" className={'text-right'}>
                    <a href='/citations' className="see-more-link">
                      See More&nbsp;&nbsp;&nbsp;
                      <FontAwesomeIcon icon={faArrowRight} size={10} className="arrow-icon" style={{transition: 'transform 0.2s ease'}} />
                    </a>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody style={{padding: 0, paddingBottom: 12, height: 235, maxHeight: 235, overflowY: 'auto'}}>
                {props.gettingCitations ? (
                  <Row>
                    <Col className={'text-center'}>
                      <ActivityIndicator />
                    </Col>
                  </Row>
                ) : citations?.length > 0 ? (
                  <Row>
                    <Col sm={6} xs={12}>
                      <div style={{padding: 16, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <Chart
                          type='doughnut'
                          datasetIdKey='id'
                          options={{
                            responsive: true,
                            cutout: '60%',
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: false
                              },
                              title: {
                                display: false,
                              },
                              tooltip: {
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                titleColor: '#495057',
                                bodyColor: '#495057',
                                borderColor: '#e9ecef',
                                borderWidth: 1,
                                padding: 10,
                                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                                cornerRadius: 4,
                                displayColors: true,
                                callbacks: {
                                  label: function(context) {
                                    let value = context.raw;
                                    return `${context.label}: $${value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                                  }
                                }
                              }
                            }
                          }}
                          data={{
                            labels: ['Paid', 'Unpaid', 'Void', 'Closed'],
                            datasets: [
                              {
                                id: 1,
                                label: '',
                                data: [donutPaid, donutUnpaid, donutVoid, donutClosed],
                                backgroundColor: ['rgba(33, 170, 115, 0.8)', 'rgba(230, 73, 46, 0.8)', 'rgba(108, 117, 125, 0.7)', 'rgba(0, 123, 255, 0.7)'],
                                borderColor: ['rgba(33, 170, 115, 0.2)', 'rgba(230, 73, 46, 0.2)', 'rgba(108, 117, 125, 0.2)', 'rgba(0, 123, 255, 0.2)'],
                                borderWidth: 1,
                                hoverOffset: 4
                              },
                            ],
                          }}
                        />
                      </div>
                    </Col>
                    <Col sm={6} xs={12}>
                      <div style={{padding: '16px 16px 12px 0', height: '100%'}}>
                        <div style={{marginBottom: 10, borderBottom: '1px solid #EAEDF3', paddingBottom: 10}}> 
                          <div className="text-right">
                            <span style={{fontSize: 24, lineHeight: '24px', fontWeight: 500}}>${(donutPaid + donutUnpaid + donutVoid + donutClosed).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          <div className="text-right">
                            <span style={{fontSize: 11, color: '#818ea3'}}>ISSUED THIS PERIOD</span>
                          </div>
                        </div>
                        
                        <div className="mt-3">
                          <div className="d-flex align-items-center mb-2">
                            <span style={{width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(33, 170, 115, 0.8)', display: 'inline-block', marginRight: 8}}></span>
                            <span style={{fontSize: 12, width: '45%'}}>PAID</span>
                            <span style={{fontSize: 12, width: '45%'}} className="text-right">${(donutPaid).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          <div className="d-flex align-items-center mb-2">
                            <span style={{width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(230, 73, 46, 0.8)', display: 'inline-block', marginRight: 8}}></span>
                            <span style={{fontSize: 12, width: '45%'}}>UNPAID</span>
                            <span style={{fontSize: 12, width: '45%'}} className="text-right">${(donutUnpaid).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          <div className="d-flex align-items-center mb-2">
                            <span style={{width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(108, 117, 125, 0.7)', display: 'inline-block', marginRight: 8}}></span>
                            <span style={{fontSize: 12, width: '45%'}}>VOID</span>
                            <span style={{fontSize: 12, width: '45%'}} className="text-right">${(donutVoid).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          <div className="d-flex align-items-center">
                            <span style={{width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(0, 123, 255, 0.7)', display: 'inline-block', marginRight: 8}}></span>
                            <span style={{fontSize: 12, width: '45%'}}>CLOSED</span>
                            <span style={{fontSize: 12, width: '45%'}} className="text-right">${(donutClosed).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                ) : (
                  <div className="text-center p-4" style={{color: '#6c757d'}}>
                    <FontAwesomeIcon icon={faCheckCircle} size="2x" style={{color: '#21AA73', marginBottom: '10px', opacity: 0.8}} />
                    <h6>No citations found</h6>
                    <p style={{fontSize: '12px'}}>No citations have been issued during the selected period.</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col md="12" className="col-12 mb-4">
            <Card className={'h-100'} style={{borderRadius: '6px', boxShadow: '0 1px 10px rgba(0,0,0,0.05)', border: '1px solid #eaedf3'}}>
              <CardHeader className="border-bottom">
                        <Row>
                  <Col>
                    <h6 className="m-0 font-weight-bold d-flex align-items-center">
                      <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" style={{color: '#007bff', opacity: 0.8}} />
                      Citations Summary
                    </h6>
                    <small className="text-muted">Quantity breakdown by status</small>
                          </Col>
                        </Row>
              </CardHeader>
              <CardBody>
                        <Row>
                  <Col xs="6" md="3" className="text-center mb-3 mb-md-0">
                    <div className="p-3" style={{borderRadius: 4, transition: 'all 0.2s ease'}}>
                      <h6 style={{fontSize: 11, color: '#818ea3', marginBottom: 10}}>TOTAL</h6>
                      <div style={{fontSize: 22}}>{(statsPaid + statsUnpaid + statsVoid + statsClosed).toLocaleString('en-US')}</div>
                    </div>
                  </Col>
                  <Col xs="6" md="3" className="text-center mb-3 mb-md-0">
                    <div className="p-3" style={{backgroundColor: 'rgba(33, 170, 115, 0.08)', borderRadius: 6, transition: 'all 0.2s ease'}}>
                      <h6 style={{fontSize: 11, color: '#21AA73', marginBottom: 10}}>PAID</h6>
                      <div style={{fontSize: 22, color: '#21AA73'}}>{statsPaid.toLocaleString('en-US')}</div>
                    </div>
                  </Col>
                  <Col xs="6" md="3" className="text-center">
                    <div className="p-3" style={{backgroundColor: 'rgba(230, 73, 46, 0.08)', borderRadius: 6, transition: 'all 0.2s ease'}}>
                      <h6 style={{fontSize: 11, color: '#E6492E', marginBottom: 10}}>UNPAID</h6>
                      <div style={{fontSize: 22, color: '#E6492E'}}>{statsUnpaid.toLocaleString('en-US')}</div>
                    </div>
                  </Col>
                  <Col xs="6" md="3" className="text-center">
                    <div className="p-3" style={{backgroundColor: 'rgba(0, 123, 255, 0.08)', borderRadius: 6, transition: 'all 0.2s ease'}}>
                      <h6 style={{fontSize: 11, color: '#007bff', marginBottom: 10}}>WARNINGS</h6>
                      <div style={{fontSize: 22, color: '#007bff'}}>{statsWarnings.toLocaleString('en-US')}</div>
                    </div>
                          </Col>
                </Row>
              </CardBody>
            </Card>
                          </Col>
                        </Row>
        
        <Row className="mb-4">
          <Col md="12" className="col-12 mb-4">
            <Card className={'h-100'} style={{borderRadius: '6px', boxShadow: '0 1px 10px rgba(0,0,0,0.05)', border: '1px solid #eaedf3'}}>
              <CardHeader className="border-bottom">
                        <Row>
                  <Col>
                    <h6 className="m-0 font-weight-bold d-flex align-items-center">
                      <FontAwesomeIcon icon={faCarAlt} className="mr-2" style={{color: '#ff9800', opacity: 0.8}} />
                      Scan Activity
                    </h6>
                    <small className="text-muted">Scan and violation statistics</small>
                          </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {props.gettingDailyScans ? (
                  <Row>
                    <Col className={'text-center'}>
                      <ActivityIndicator />
                          </Col>
                        </Row>
                ) : (
                        <Row>
                    <Col xs="6" md="3" className="text-center mb-3 mb-md-0">
                      <div className="p-3" style={{borderRadius: 6, transition: 'all 0.2s ease'}}>
                        <h6 style={{fontSize: 11, color: '#818ea3', marginBottom: 10}}>PLATES SCANNED</h6>
                        <div style={{fontSize: 22}}>{(calculateScanStats().scan + calculateScanStats().violation).toLocaleString('en-US')}</div>
                      </div>
                    </Col>
                    <Col xs="6" md="3" className="text-center mb-3 mb-md-0">
                      <div className="p-3" style={{backgroundColor: 'rgba(255, 152, 0, 0.08)', borderRadius: 6, transition: 'all 0.2s ease'}}>
                        <h6 style={{fontSize: 11, color: '#ff9800', marginBottom: 10}}>VIOLATIONS</h6>
                        <div style={{fontSize: 22, color: '#ff9800'}}>{calculateScanStats().violation.toLocaleString('en-US')}</div>
                      </div>
                          </Col>
                    <Col xs="6" md="3" className="text-center mb-3 mb-md-0">
                      <div className="p-3" style={{backgroundColor: 'rgba(0, 123, 255, 0.08)', borderRadius: 6, transition: 'all 0.2s ease'}}>
                        <h6 style={{fontSize: 11, color: '#007bff', marginBottom: 10}}>WARNINGS</h6>
                        <div style={{fontSize: 22, color: '#007bff'}}>{statsWarnings.toLocaleString('en-US')}</div>
                      </div>
                          </Col>
                    <Col xs="6" md="3" className="text-center mb-3 mb-md-0">
                      <div className="p-3" style={{backgroundColor: 'rgba(230, 73, 46, 0.08)', borderRadius: 6, transition: 'all 0.2s ease'}}>
                        <h6 style={{fontSize: 11, color: '#E6492E', marginBottom: 10}}>CITATIONS ISSUED</h6>
                        <div style={{fontSize: 22, color: '#E6492E'}}>{(statsPaid + statsUnpaid + statsVoid + statsClosed).toLocaleString('en-US')}</div>
                      </div>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col className="mb-4">
            <Card className={'h-100'} style={{borderRadius: '6px', boxShadow: '0 1px 10px rgba(0,0,0,0.05)', border: '1px solid #eaedf3'}}>
              <CardHeader className="border-bottom">
                <Row>
                  <Col>
                    <h6 className="m-0 font-weight-bold d-flex align-items-center">
                      <FontAwesomeIcon icon={faChartBar} className="mr-2" style={{color: '#21AA73', opacity: 0.8}} />
                      Daily Plate Scans
                    </h6>
                    <small className="text-muted">Plate scan volume by day</small>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {props.gettingDailyScans ? (
                  <Row>
                    <Col className={'text-center'}>
                      <ActivityIndicator />
                    </Col>
                  </Row>
                ) : (
                  <Chart
                    type='bar'
                    datasetIdKey='id'
                    options={chartOptions}
                    data={{
                      labels: getDateLabels(),
                      datasets: [
                        {
                          id: 1,
                          label: 'Daily Scans',
                          data: dailyScans.map(scan => scan.v),
                          backgroundColor: 'rgba(33, 170, 115, 0.7)',
                          borderColor: 'rgba(33, 170, 115, 0.9)',
                          borderWidth: 1,
                          borderRadius: 4,
                          hoverBackgroundColor: 'rgba(33, 170, 115, 0.9)',
                          gridLines: false,
                        },
                      ],
                    }}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
       </>
     )} 
      
      {/* Add the DisputeQuickView modal */}
      <DisputeQuickView
        isOpen={showDisputeModal}
        toggle={() => setShowDisputeModal(!showDisputeModal)}
        citation={selectedDispute}
        onAccept={handleAcceptDispute}
        onReject={handleRejectDispute}
        onAdjust={handleAdjustDispute}
        onViewFull={handleViewFullCitation}
        users={props.users}
        isSuperAdmin={props.user?.type === 'super'}
      />
      
      {/* Welcome Improvements Modal */}
      <Modal
        open={showWelcomeModal}
        toggle={closeWelcomeModal}
        size="md"
        centered
      >
        <ModalHeader className="border-bottom" toggle={closeWelcomeModal} style={{ borderColor: '#eaedf3' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              backgroundColor: 'rgba(33, 170, 115, 0.1)', 
              color: '#21AA73', 
              width: '40px', 
              height: '40px', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginRight: '12px'
            }}>
              <FontAwesomeIcon icon={faRocket} size="lg" />
            </div>
            <div>
              <h5 style={{ margin: 0, fontWeight: 600, color: '#3D3C3E' }}>Welcome to the Improved EnforcePlus</h5>
            </div>
          </div>
        </ModalHeader>
        
        <ModalBody style={{ padding: '1.5rem' }}>
          <p style={{ fontSize: '15px', lineHeight: '1.5', color: '#495057', marginBottom: '16px' }}>
            We've made significant improvements to EnforcePlus to provide you with a better experience and more powerful features.
          </p>
          
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div style={{ 
                color: '#21AA73', 
                marginRight: '15px',
                marginTop: '2px' 
              }}>
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
              <div>
                <h6 style={{ fontWeight: 600, fontSize: '14px', marginBottom: '5px', color: '#3D3C3E' }}>Enhanced User Interface</h6>
                <p style={{ fontSize: '14px', color: '#495057', margin: 0 }}>
                  A more modern, intuitive design makes it easier to navigate and complete tasks efficiently.
                </p>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div style={{ 
                color: '#21AA73', 
                marginRight: '15px',
                marginTop: '2px' 
              }}>
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
              <div>
                <h6 style={{ fontWeight: 600, fontSize: '14px', marginBottom: '5px', color: '#3D3C3E' }}>Better Performance</h6>
                <p style={{ fontSize: '14px', color: '#495057', margin: 0 }}>
                  Faster loading times and more responsive interactions for a smoother experience.
                </p>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div style={{ 
                color: '#21AA73', 
                marginRight: '15px',
                marginTop: '2px' 
              }}>
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
              <div>
                <h6 style={{ fontWeight: 600, fontSize: '14px', marginBottom: '5px', color: '#3D3C3E' }}>New Features</h6>
                <p style={{ fontSize: '14px', color: '#495057', margin: 0 }}>
                  Exciting new capabilities to help you manage citations, permits, and reports more effectively.
                </p>
              </div>
            </div>
          </div>
          
          <div style={{ 
            backgroundColor: 'rgba(33, 170, 115, 0.05)', 
            padding: '16px', 
            borderRadius: '6px', 
            borderLeft: '3px solid #21AA73',
            marginTop: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div style={{ 
                color: '#21AA73', 
                marginRight: '15px',
                marginTop: '2px' 
              }}>
                <FontAwesomeIcon icon={faQuestionCircle} />
              </div>
              <div>
                <h6 style={{ fontWeight: 600, fontSize: '14px', marginBottom: '5px', color: '#3D3C3E' }}>Questions or Need Help?</h6>
                <p style={{ fontSize: '14px', color: '#495057', margin: 0 }}>
                  Our customer success team is ready to assist you with any questions you may have.
                  <br /><br />
                  Contact us at: <a href="mailto:support@oobeo.com" style={{ color: '#21AA73', fontWeight: 500 }}>support@oobeo.com</a>
                </p>
              </div>
            </div>
          </div>
        </ModalBody>
        
        <ModalFooter style={{ padding: '1rem 1.5rem', borderTop: '1px solid #eaedf3', display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            theme="light" 
            onClick={() => closeWelcomeModal(false)}
            style={{ 
              fontWeight: '500', 
              padding: '0.5rem 1.2rem',
              color: '#495057',
              borderColor: '#dfe3e9'
            }}
          >
            Dismiss for Now
          </Button>
          <Button 
            theme="accent" 
            onClick={() => closeWelcomeModal(true)}
            style={{ 
              fontWeight: '500', 
              padding: '0.5rem 1.2rem',
              boxShadow: '0 3px 10px rgba(33, 170, 115, 0.2)'
            }}
          >
            Don't Show Again
          </Button>
        </ModalFooter>
      </Modal>

      {/* Date Range Picker Modal */}
      <Modal 
        open={showDatePickerModal} 
        toggle={() => setShowDatePickerModal(false)}
        size="lg"
        className="date-picker-modal"
      >
        <ModalHeader toggle={() => setShowDatePickerModal(false)}>
          <FontAwesomeIcon icon={faCalendar} className="mr-2" style={{ color: '#21AA73' }} />
          Select Date Range
        </ModalHeader>
        <ModalBody className="p-4">
          <Row>
            <Col md="8">
              <Card>
                <CardBody className="p-4">
                  <div className="d-flex" style={{ gap: '24px' }}>
                    <div className="flex-grow-1">
                      <Button 
                        block
                        theme="white"
                        style={{
                          borderColor: '#e1e5eb',
                          backgroundColor: '#fff',
                          textAlign: 'left',
                          position: 'relative',
                          height: '38px'
                        }}
                        onClick={() => setActiveCalendar(activeCalendar === 'start' ? null : 'start')}
                      >
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faCalendar} className="mr-2" style={{ color: '#21AA73' }} />
                          <span style={{ color: '#495057' }}>
                            {startDate ? moment(startDate).format('MMM D, YYYY') : 'Select start date'}
                          </span>
                        </div>
                      </Button>
                      {activeCalendar === 'start' && (
                        <div style={{ 
                          position: 'absolute', 
                          zIndex: 1000,
                          marginTop: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
                          border: '1px solid #eaedf3'
                        }}>
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => {
                              handleDateChange('start', date);
                              setActiveCalendar('end');
                            }}
                            maxDate={endDate || new Date()}
                            inline
                            calendarClassName="single-date-calendar"
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <Button 
                        block
                        theme="white"
                        style={{
                          borderColor: '#e1e5eb',
                          backgroundColor: '#fff',
                          textAlign: 'left',
                          position: 'relative',
                          height: '38px'
                        }}
                        onClick={() => setActiveCalendar(activeCalendar === 'end' ? null : 'end')}
                      >
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faCalendar} className="mr-2" style={{ color: '#21AA73' }} />
                          <span style={{ color: '#495057' }}>
                            {endDate ? moment(endDate).format('MMM D, YYYY') : 'Select end date'}
                          </span>
                        </div>
                      </Button>
                      {activeCalendar === 'end' && (
                        <div style={{ 
                          position: 'absolute', 
                          zIndex: 1000,
                          marginTop: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
                          border: '1px solid #eaedf3'
                        }}>
                          <DatePicker
                            selected={endDate}
                            onChange={(date) => {
                              handleDateChange('end', date);
                              setActiveCalendar(null);
                            }}
                            minDate={startDate}
                            maxDate={new Date()}
                            inline
                            calendarClassName="single-date-calendar"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <style>
                    {`
                      .single-date-calendar .react-datepicker__day--selected {
                        background-color: #21AA73 !important;
                        border-radius: 50% !important;
                      }
                      .single-date-calendar .react-datepicker__day--in-range,
                      .single-date-calendar .react-datepicker__day--in-selecting-range {
                        background-color: transparent !important;
                        color: inherit !important;
                      }
                      .single-date-calendar .react-datepicker__day:hover {
                        background-color: rgba(33, 170, 115, 0.1) !important;
                        border-radius: 50% !important;
                      }
                    `}
                  </style>
                </CardBody>
              </Card>
            </Col>
            <Col md="4">
              <Card>
                <CardHeader className="border-bottom">
                  <h6 className="m-0">Quick Select</h6>
                </CardHeader>
                <CardBody className="p-3">
                  <div className="d-flex flex-column" style={{ gap: '8px' }}>
                    <Button 
                      theme="light" 
                      size="sm" 
                      block
                      style={{
                        fontSize: '13px',
                        color: '#21AA73',
                        borderColor: 'rgba(33, 170, 115, 0.3)',
                        textAlign: 'left',
                        padding: '10px 12px'
                      }} 
                      onClick={() => {
                        setStartDate(startOfDay(new Date()));
                        setEndDate(endOfDay(new Date()));
                        setShowDatePickerModal(false);
                      }}
                    >
                      Today
                    </Button>
                    <Button 
                      theme="light" 
                      size="sm" 
                      block
                      style={{
                        fontSize: '13px',
                        color: '#21AA73',
                        borderColor: 'rgba(33, 170, 115, 0.3)',
                        textAlign: 'left',
                        padding: '10px 12px'
                      }} 
                      onClick={() => {
                        setStartDate(startOfWeek(new Date()));
                        setEndDate(endOfWeek(new Date()));
                        setShowDatePickerModal(false);
                      }}
                    >
                      This Week
                    </Button>
                    <Button 
                      theme="light" 
                      size="sm" 
                      block
                      style={{
                        fontSize: '13px',
                        color: '#21AA73',
                        borderColor: 'rgba(33, 170, 115, 0.3)',
                        textAlign: 'left',
                        padding: '10px 12px'
                      }} 
                      onClick={() => {
                        setStartDate(startOfMonth(new Date()));
                        setEndDate(endOfMonth(new Date()));
                        setShowDatePickerModal(false);
                      }}
                    >
                      This Month
                    </Button>
                    <Button 
                      theme="light" 
                      size="sm" 
                      block
                      style={{
                        fontSize: '13px',
                        color: '#21AA73',
                        borderColor: 'rgba(33, 170, 115, 0.3)',
                        textAlign: 'left',
                        padding: '10px 12px'
                      }} 
                      onClick={() => {
                        setStartDate(sub(new Date(), { days: 7 }));
                        setEndDate(new Date());
                        setShowDatePickerModal(false);
                      }}
                    >
                      Last 7 Days
                    </Button>
                    <Button 
                      theme="light" 
                      size="sm" 
                      block
                      style={{
                        fontSize: '13px',
                        color: '#21AA73',
                        borderColor: 'rgba(33, 170, 115, 0.3)',
                        textAlign: 'left',
                        padding: '10px 12px'
                      }} 
                      onClick={() => {
                        setStartDate(sub(new Date(), { days: 30 }));
                        setEndDate(new Date());
                        setShowDatePickerModal(false);
                      }}
                    >
                      Last 30 Days
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default Dashboard;
